/**
 * CONTROL ROOM › VILKÅR (Atle 02.11): de fire juridiske dokumentene, redigert og publisert som en ny
 * revisjon (`/v1/platform/legal`). Bare for plattformadmin.
 *
 * En revisjon publiseres for begge språk samtidig, med samme versjonsnummer. Samtykkesperren sammenligner
 * versjonen på tvers av språk (`IdentityService.pendingConsents`), så et nummer som bare fantes på norsk,
 * ville bedt en engelsk bruker om å godta noe hen alt hadde godtatt.
 */
import { z } from 'zod';

import { termsKindSchema } from '../enums.js';

/** Samme form som adressene i appen godtar (`legalDocumentParamsSchema`): 0.1, 0.2, 1.0 … */
export const LEGAL_REVISION_PATTERN = /^[0-9A-Za-z][0-9A-Za-z.-]{0,39}$/;

export const platformLegalTextSchema = z
  .object({
    /** Kort tittel, som på samtykkesiden («Brukervilkår»). */
    title: z.string(),
    /** Markdown, med `# Tittel` først. Tom når versjonen ble publisert uten tekst (2026-08-01). */
    text: z.string(),
  })
  .meta({ id: 'PlatformLegalText' });
export type PlatformLegalText = z.infer<typeof platformLegalTextSchema>;

export const platformLegalVersionSchema = z
  .object({
    version: z.string(),
    effectiveFrom: z.iso.datetime(),
    current: z.boolean(),
  })
  .meta({ id: 'PlatformLegalVersion' });
export type PlatformLegalVersion = z.infer<typeof platformLegalVersionSchema>;

export const platformLegalDocumentSchema = z
  .object({
    kind: termsKindSchema,
    /** Den gjeldende revisjonen, som redigeringen starter fra. */
    current: z
      .object({
        version: z.string(),
        effectiveFrom: z.iso.datetime(),
        nb: platformLegalTextSchema,
        en: platformLegalTextSchema,
      })
      .nullable(),
    /** Alle revisjonene, nyeste først. */
    versions: z.array(platformLegalVersionSchema),
  })
  .meta({ id: 'PlatformLegalDocument' });
export type PlatformLegalDocument = z.infer<typeof platformLegalDocumentSchema>;

export const platformLegalListResponseSchema = z
  .object({ documents: z.array(platformLegalDocumentSchema) })
  .meta({ id: 'PlatformLegalListResponse' });
export type PlatformLegalListResponse = z.infer<typeof platformLegalListResponseSchema>;

const legalTextBody = z.string().trim().min(20).max(200_000).refine((text) => text.startsWith('# '), {
  message: 'The text must start with «# Title».',
});

export const publishLegalRevisionRequestSchema = z
  .object({
    kind: termsKindSchema,
    version: z.string().trim().regex(LEGAL_REVISION_PATTERN),
    nb: legalTextBody,
    en: legalTextBody,
  })
  .meta({ id: 'PublishLegalRevisionRequest' });
export type PublishLegalRevisionRequest = z.infer<typeof publishLegalRevisionRequestSchema>;
